// B-101 — an office's pending notes, in the closing detail: the operator's NOTAS PENDIENTES, for
// this office. Open first, then the closed and annulled ones, each with its kind, amount, since
// when, how it came (automatic, by hand, loaded) and its observation.
//
// "Nuevo" opens a note by hand — manual, a transaction in flight or a wrong causal — with its amount
// and an observation; "Cerrar" closes one with an observation. B-102 — an open "Transacción en
// vuelo" offers "Correo al banco" (`CorreoTxEnVuelo`), and says once it was sent. B-103 — a note the
// office has to correct (`noticeTarget: "OFFICE"`: a stamp too many, one missing, a wrong causal)
// offers "Correo a la oficina" (`CorreoAOficina`), written by the backend from the client's template
// and previewed before it goes; a manual note offers none. Both need `cierres.alerts.resolve` on the office (`puedeCambiar`), which is what
// the backend asks; anyone else reads the list.
//
// B-108 — each note says how many days it has been open (drawn as old past a week), the list filters
// by kind, status and age once it holds more than one note, and closing one asks first: the
// observation, then a confirmation that says what stops counting, before anything is sent.
//
// Drawn only for a client with a day reconciliation: for any other, `listCierrePendingItems`
// answers 404 and nothing is shown.
import { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { Button, Spinner, cn } from "@mantis-sas/ui";

import {
  closeCierrePendingItem,
  createCierrePendingItem,
  listCierrePendingItems,
  sendCierrePendingItemNotice,
} from "../../../ApiCalls/mantisBusiness";
import { correctionError } from "../../../utils/cierreCorrections";
import { fechaDMY, fechaHoraBogota, montoLegible } from "../../../utils/cierrePanel";
import { getColombiaDateString } from "../../../utils/dateUtils";
import {
  DIAS_VIEJA,
  ESTADOS_PENDIENTE,
  ORIGENES_PENDIENTE,
  TIPOS_PENDIENTE,
  diasAbierta,
  erroresDeNuevoPendiente,
  filtrarPendientes,
  hayFiltros,
  montoEscrito,
  opcionesDePendientes,
  textoDias,
  tipoDePendiente,
} from "../../../utils/cierreCuadreDia";
import { FiltrosPendientes } from "./FiltrosPendientes";
import { CorreoTxEnVuelo } from "./CorreoTxEnVuelo";
import { CorreoAOficina } from "./CorreoAOficina";
import { guia } from "../../../utils/cierreTours";

// The kinds a person opens by hand; the other two open on their own, from the closing's alerts.
const TIPOS_A_MANO = ["MANUAL", "TX_EN_VUELO", "CAUSAL_ERRADA"];

// How long a note has been open, or was: drawn as old once an open one passes a week.
function DiasDeNota({ item, hoy }) {
  const dias = diasAbierta(item, hoy);
  const abierto = item.status === "OPEN";
  return (
    <span
      className={cn(
        "block text-[10.5px] tabular-nums",
        abierto && dias > DIAS_VIEJA ? "font-semibold text-brinks-warning" : "text-brinks-muted-foreground",
      )}
    >
      {abierto ? `abierto ${textoDias(dias)}` : `estuvo abierto ${textoDias(dias)}`}
    </span>
  );
}

export function PendientesOficina({
  token, mantisBusinessId, clientCode, officeCode, puedeCambiar,
}) {
  const [items, setItems] = useState(null);
  const [intento, setIntento] = useState(0);
  const [nuevo, setNuevo] = useState(null); // { monto, nota } while the form is open
  const [cerrando, setCerrando] = useState(null); // { id, nota, confirmar }
  const [correo, setCorreo] = useState(null); // the id whose email is open
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [filtros, setFiltros] = useState({});
  const [sinRed, setSinRed] = useState(false);
  const hoy = useMemo(() => getColombiaDateString(), []);

  useEffect(() => {
    let activo = true;
    if (!clientCode || !officeCode) return undefined;
    listCierrePendingItems(token, mantisBusinessId, clientCode, { officeCode })
      .then((res) => {
        if (!activo) return;
        setSinRed(false);
        setItems(res?.success ? res.data.items : null);
      })
      .catch(() => {
        // B-126 — the network, not "this client keeps no notes": said, with a way to try again.
        if (activo) setSinRed(true);
      });
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, clientCode, officeCode, intento]);

  if (sinRed && !items) {
    return (
      <section role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-[12px] border border-brinks-warning-border bg-brinks-warning-soft px-3.5 py-2.5 text-[12px] text-brinks-warning">
        <span>No se pudieron cargar los pendientes de la oficina. Revisa la conexión.</span>
        <Button variant="outline" size="sm" className="h-7" onClick={() => setIntento((n) => n + 1)}>Reintentar</Button>
      </section>
    );
  }
  if (!items) return null;

  const enviar = async (accion) => {
    setEnviando(true);
    setError(null);
    try {
      const res = await accion();
      if (res?.success) {
        setNuevo(null);
        setCerrando(null);
        setIntento((n) => n + 1);
      } else {
        setError(correctionError(res));
      }
    } catch (e) {
      setError("No se pudo guardar el pendiente.");
    }
    setEnviando(false);
  };

  const errores = nuevo ? erroresDeNuevoPendiente(nuevo) : [];
  const visibles = filtrarPendientes(items, filtros, hoy);
  const abiertos = items.filter((item) => item.status === "OPEN").length;

  return (
    <section {...guia("Notas pendientes de la oficina", "Las notas abiertas de la oficina y cuántos días llevan. \"Nuevo\" crea una nota, \"Cerrar\" la cierra con una observación y, según el tipo, puedes enviar el correo al banco o a la oficina.")} className="overflow-hidden rounded-[12px] border border-brinks-border-strong bg-card shadow-brinks-card">
      <div className="flex items-center justify-between border-b bg-brinks-surface px-3.5 py-2.5">
        <div>
          <h3 className="text-[12px] font-semibold text-brinks-foreground">
            Pendientes de la oficina
            {abiertos ? (
              <span className="ml-1.5 rounded-full border border-brinks-warning-border bg-brinks-warning-soft px-1.5 py-0.5 text-[10.5px] font-semibold text-brinks-warning">
                {abiertos === 1 ? "1 abierto" : `${abiertos} abiertos`}
              </span>
            ) : null}
          </h3>
          <p className="mt-0.5 text-[10.5px] text-brinks-muted-foreground">
            Notas por corregir que explican la diferencia de la cuenta hasta que el banco las compense
          </p>
        </div>
        {puedeCambiar && !nuevo ? (
          <Button variant="outline" size="sm" className="h-7 gap-1" onClick={() => setNuevo({ tipo: "MANUAL", monto: "", nota: "" })}>
            <Plus className="h-3.5 w-3.5" /> Nuevo
          </Button>
        ) : null}
      </div>

      {error ? <p role="alert" className="px-3.5 pt-2 text-[12px] text-brinks-warning">{error}</p> : null}

      {nuevo ? (
        <div className="space-y-2 border-b px-3.5 py-2.5 text-[12px]">
          <label className="block">
            Tipo
            <select
              aria-label="Tipo"
              className="mt-1 block w-full rounded-[6px] border px-2 py-1"
              value={nuevo.tipo}
              onChange={(e) => setNuevo({ ...nuevo, tipo: e.target.value })}
            >
              {TIPOS_A_MANO.map((tipo) => <option key={tipo} value={tipo}>{TIPOS_PENDIENTE[tipo]}</option>)}
            </select>
          </label>
          <label className="block">
            Monto (con su signo)
            <input
              aria-label="Monto"
              className="mt-1 block w-full rounded-[6px] border px-2 py-1"
              value={nuevo.monto}
              onChange={(e) => setNuevo({ ...nuevo, monto: e.target.value })}
              placeholder="-5.274.179"
            />
          </label>
          <label className="block">
            Observación
            <textarea
              aria-label="Observación"
              className="mt-1 block w-full rounded-[6px] border px-2 py-1"
              maxLength={1000}
              value={nuevo.nota}
              onChange={(e) => setNuevo({ ...nuevo, nota: e.target.value })}
            />
          </label>
          {errores.length && (nuevo.monto || nuevo.nota) ? (
            <p className="text-brinks-muted-foreground">{errores.join(" ")}</p>
          ) : null}
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={errores.length > 0 || enviando}
              onClick={() => enviar(() => createCierrePendingItem(token, mantisBusinessId, clientCode, {
                officeCode, kind: nuevo.tipo || "MANUAL", amount: montoEscrito(nuevo.monto), note: nuevo.nota.trim(),
              }))}
            >
              Guardar
            </Button>
            <Button size="sm" variant="outline" disabled={enviando} onClick={() => setNuevo(null)}>Cancelar</Button>
          </div>
        </div>
      ) : null}

      {items.length > 1 ? (
        <div className="border-b px-3.5 py-2">
          <FiltrosPendientes
            filtros={filtros}
            onChange={setFiltros}
            opciones={opcionesDePendientes(items)}
            campos={["tipo", "estado", "antiguedad"]}
          />
        </div>
      ) : null}

      {visibles.length ? (
        <ul className="divide-y divide-brinks-border px-3.5">
          {visibles.map((item) => (
            <li key={item.id} className="py-2 text-[12px]">
              <div className="flex items-start justify-between gap-3">
                <span className="min-w-0">
                  <span className="block font-medium">{tipoDePendiente(item.kind)}</span>
                  <span className="block text-[10.5px] text-brinks-muted-foreground">
                    {[
                      `desde ${fechaDMY(item.openedOn)}`,
                      ORIGENES_PENDIENTE[item.origin] || item.origin,
                      item.status !== "OPEN" ? `${ESTADOS_PENDIENTE[item.status]} el ${fechaDMY(item.closedOn)} por ${item.closedBy}` : null,
                    ].filter(Boolean).join(" · ")}
                  </span>
                  {item.note ? <span className="block text-[11px]">{item.note}</span> : null}
                  {item.noticeSentAt ? (
                    <span className="block text-[10.5px] text-brinks-success">
                      {item.kind === "TX_EN_VUELO" ? "Correo al banco enviado" : "Correo a la oficina enviado"} {fechaHoraBogota(item.noticeSentAt)}
                      {item.noticeSentBy ? ` por ${item.noticeSentBy}` : ""}
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-right">
                  <span className="block font-semibold tabular-nums">{montoLegible(item.amount)}</span>
                  <span className="block text-[10.5px] text-brinks-muted-foreground">{ESTADOS_PENDIENTE[item.status] || item.status}</span>
                  <DiasDeNota item={item} hoy={hoy} />
                </span>
              </div>
              {/* The actions in a row of their own under the note, side by side while they fit. */}
              {puedeCambiar && item.status === "OPEN" && (correo !== item.id || cerrando?.id !== item.id) ? (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {item.kind === "TX_EN_VUELO" && correo !== item.id ? (
                    <Button variant="outline" size="sm" className="h-7" onClick={() => setCorreo(item.id)}>
                      Correo al banco
                    </Button>
                  ) : null}
                  {item.noticeTarget === "OFFICE" && correo !== item.id ? (
                    <Button variant="outline" size="sm" className="h-7" onClick={() => setCorreo(item.id)}>
                      Correo a la oficina
                    </Button>
                  ) : null}
                  {cerrando?.id !== item.id ? (
                    <Button variant="outline" size="sm" className="h-7" onClick={() => setCerrando({ id: item.id, nota: "", confirmar: false })}>
                      Cerrar
                    </Button>
                  ) : null}
                </div>
              ) : null}
              {correo === item.id && item.noticeTarget === "OFFICE" ? (
                <CorreoAOficina
                  titulo="Correo a la oficina: qué timbrar y cuándo"
                  enviar={(preview, { to, comment }) => sendCierrePendingItemNotice(token, mantisBusinessId, clientCode, {
                    id: item.id, to, comment, preview,
                  })}
                  onCancelar={() => setCorreo(null)}
                  onEnviado={() => {
                    setCorreo(null);
                    setIntento((n) => n + 1);
                  }}
                />
              ) : null}
              {correo === item.id && item.kind === "TX_EN_VUELO" ? (
                <CorreoTxEnVuelo
                  token={token}
                  mantisBusinessId={mantisBusinessId}
                  clientCode={clientCode}
                  item={item}
                  onCancelar={() => setCorreo(null)}
                  onEnviado={() => {
                    setCorreo(null);
                    setIntento((n) => n + 1);
                  }}
                />
              ) : null}
              {cerrando?.id === item.id && !cerrando.confirmar ? (
                <div className="mt-2 space-y-2">
                  <textarea
                    aria-label="Observación del cierre"
                    className="block w-full rounded-[6px] border px-2 py-1"
                    maxLength={1000}
                    value={cerrando.nota}
                    onChange={(e) => setCerrando({ ...cerrando, nota: e.target.value })}
                    placeholder="Por qué se cierra"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      disabled={!cerrando.nota.trim() || enviando}
                      onClick={() => setCerrando({ ...cerrando, confirmar: true })}
                    >
                      Cerrar pendiente
                    </Button>
                    <Button size="sm" variant="outline" disabled={enviando} onClick={() => setCerrando(null)}>Cancelar</Button>
                  </div>
                </div>
              ) : null}
              {cerrando?.id === item.id && cerrando.confirmar ? (
                <div
                  role="alertdialog"
                  aria-labelledby={`cerrar-${item.id}`}
                  className="mt-2 space-y-2 rounded-[8px] border border-brinks-warning-border bg-brinks-warning-soft px-3 py-2"
                >
                  <p id={`cerrar-${item.id}`} className="font-semibold text-brinks-foreground">
                    ¿Cerrar la nota «{tipoDePendiente(item.kind)}» de {montoLegible(item.amount)}?
                  </p>
                  <p className="text-brinks-muted-foreground">
                    Deja de explicar la diferencia de la cuenta desde hoy. Observación: «{cerrando.nota.trim()}»
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      autoFocus
                      disabled={enviando}
                      onClick={() => enviar(() => closeCierrePendingItem(
                        token, mantisBusinessId, clientCode, item.id, cerrando.nota.trim(),
                      ))}
                    >
                      {enviando ? <Spinner size="sm" /> : "Confirmar cierre"}
                    </Button>
                    <Button size="sm" variant="outline" disabled={enviando} onClick={() => setCerrando({ ...cerrando, confirmar: false })}>
                      Volver
                    </Button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-3.5 py-2.5 text-[12px] text-brinks-muted-foreground">
          {items.length && hayFiltros(filtros) ? "Ningún pendiente cumple los filtros." : "Esta oficina no tiene pendientes."}
        </p>
      )}
    </section>
  );
}

export default PendientesOficina;
